import { BellIcon, BellRingingIcon, ChecksIcon } from '@phosphor-icons/react';
import type { NotificationList, PushConfig } from '@regia/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { errorMessage, http } from '../../lib/api';
import { formatAgo } from '../../lib/format';
import { isIos, isStandalone } from '../../lib/media';
import { enablePush, getPushStatus, type PushStatus } from '../../lib/push';
import { keys } from '../../lib/query';
import { Button, cn, EmptyState, IconButton, ListGroup, PageHeader, Skeleton } from '../../ui';
import { PageBody } from '../common';

export function useNotifications() {
  return useQuery({
    queryKey: keys.notifications,
    queryFn: () => http.get<NotificationList>('/api/notifications?limit=60'),
    refetchInterval: 60_000,
  });
}

export function NotificationBell() {
  const navigate = useNavigate();
  const { data } = useNotifications();
  const unread = data?.unread ?? 0;
  return (
    <IconButton
      icon={BellIcon}
      label={unread ? `Notifiche, ${unread} da leggere` : 'Notifiche'}
      onClick={() => navigate('/notifiche')}
      badge={
        unread ? (
          <span
            className="absolute top-2 right-2 h-2.5 w-2.5 rounded-full bg-alert ring-2 ring-paper"
            aria-hidden
          />
        ) : null
      }
    />
  );
}

export function usePushConfig() {
  return useQuery({
    queryKey: keys.pushConfig,
    queryFn: () => http.get<PushConfig>('/api/push/config'),
    staleTime: Infinity,
  });
}

/** Riquadro per attivare le notifiche push su questo dispositivo. */
export function PushCard({ compact }: { compact?: boolean }) {
  const config = usePushConfig();
  const [status, setStatus] = useState<PushStatus | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (config.data) getPushStatus(config.data).then(setStatus);
  }, [config.data]);

  if (!status || status === 'on') {
    if (compact || !status) return null;
    return (
      <div className="flex items-center justify-between gap-3 rounded-[var(--radius-sheet)] bg-sheet p-4">
        <p className="text-[0.9375rem] text-ink-2">
          Le notifiche sono attive su questo dispositivo.
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={async () => {
            await http.post('/api/push/test');
            toast.success('Notifica di prova inviata');
          }}
        >
          Prova
        </Button>
      </div>
    );
  }

  let message = 'Ricevi qui promemoria e avvisi, anche con l’app chiusa.';
  let action: React.ReactNode = (
    <Button
      size="sm"
      loading={busy}
      onClick={async () => {
        if (!config.data) return;
        setBusy(true);
        try {
          await enablePush(config.data);
          setStatus('on');
          toast.success('Notifiche attivate');
        } catch (err) {
          toast.error(errorMessage(err));
        } finally {
          setBusy(false);
        }
      }}
    >
      Attiva
    </Button>
  );
  if (status === 'unsupported') {
    if (isIos() && !isStandalone()) {
      message = 'Per ricevere le notifiche sull’iPhone aggiungi prima Regia alla schermata Home.';
      action = null;
    } else {
      if (compact) return null;
      message = 'Questo browser non supporta le notifiche push.';
      action = null;
    }
  } else if (status === 'disabled-server') {
    if (compact) return null;
    message = 'Le notifiche push non sono ancora configurate sul server.';
    action = null;
  } else if (status === 'denied') {
    message =
      'Hai bloccato le notifiche: riattivale nelle impostazioni del telefono, alla voce Regia.';
    action = null;
  }

  return (
    <div className="flex items-center gap-3 rounded-[var(--radius-sheet)] bg-sheet p-4">
      <BellRingingIcon size={26} className="shrink-0 text-action" aria-hidden />
      <p className="min-w-0 flex-1 text-[0.9375rem] text-ink-2">{message}</p>
      {action}
    </div>
  );
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const list = useNotifications();
  const markAll = useMutation({
    mutationFn: () => http.post('/api/notifications/read-all'),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.notifications }),
  });

  async function open(id: string, url: string, read: boolean) {
    if (!read) {
      await http.post(`/api/notifications/${id}/read`).catch(() => undefined);
      qc.invalidateQueries({ queryKey: keys.notifications });
    }
    if (url) navigate(url);
  }

  const items = list.data?.items ?? [];
  return (
    <PageBody className="mx-auto max-w-2xl">
      <PageHeader
        title="Notifiche"
        actions={
          list.data?.unread ? (
            <Button
              variant="ghost"
              size="sm"
              icon={ChecksIcon}
              loading={markAll.isPending}
              onClick={() => markAll.mutate()}
            >
              Segna tutte come lette
            </Button>
          ) : null
        }
      />
      <div className="mb-6">
        <PushCard compact />
      </div>
      {list.isLoading ? (
        <Skeleton className="h-48 w-full rounded-[var(--radius-sheet)]" />
      ) : items.length === 0 ? (
        <EmptyState
          icon={BellIcon}
          title="Nessuna notifica"
          message="Qui arrivano promemoria, avvisi e novità dall’AI."
        />
      ) : (
        <ListGroup>
          {items.map((n) => (
            <li key={n.id}>
              <button
                type="button"
                onClick={() => open(n.id, n.url, Boolean(n.readAt))}
                className="flex w-full items-start gap-3 px-4 py-3.5 text-left transition-colors hover:bg-sheet-2"
              >
                <span
                  aria-hidden
                  className={cn(
                    'mt-2 h-2 w-2 shrink-0 rounded-full',
                    n.readAt ? 'bg-transparent' : 'bg-action',
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span
                    className={cn('block text-[0.9375rem] text-ink', !n.readAt && 'font-semibold')}
                  >
                    {n.title}
                  </span>
                  {n.body ? (
                    <span className="mt-0.5 block text-sm text-ink-2">{n.body}</span>
                  ) : null}
                  <span className="mt-1 block text-xs text-ink-3">{formatAgo(n.createdAt)}</span>
                </span>
              </button>
            </li>
          ))}
        </ListGroup>
      )}
    </PageBody>
  );
}
